import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { ChartConfiguration } from '@/config/chartConfig';
import { DEFAULT_CHART_CONFIG } from '@/config/chartConfig';
import type { ColorScheme, ColorSchemeKey } from '@/lib/colorSchemes';
import { COLOR_SCHEMES } from '@/lib/colorSchemes';

/**
 * Chart store state and actions
 */
interface ChartStore {
  // Configuration
  config: ChartConfiguration;

  // Track color scheme ID for persistence
  colorSchemeId: ColorSchemeKey;

  // Category visibility
  hiddenCategories: Set<string>;

  // Hand filter (null = show all, Set = show only these hands)
  visibleHands: Set<string> | null;

  // The range string that produced the current filter (for displaying in UI)
  rangeFilterString: string;

  // Dead cards (e.g., "Ac", "7h", "2d") - generic state that charts can interpret
  deadCards: Set<string>;

  // The dead cards input string (for displaying in UI)
  deadCardsString: string;

  // Actions
  setColorScheme: (scheme: ColorScheme, id?: ColorSchemeKey) => void;
  setUseLogScale: (useLogScale: boolean) => void;
  setShowValues: (showValues: boolean) => void;
  setShowLegend: (showLegend: boolean) => void;
  setMaxHeight: (maxHeight: number) => void;
  setSegmentLayout: (segmentLayout: ChartConfiguration['segmentLayout']) => void;
  setSegmentGap: (segmentGap: number) => void;
  setInvertLabelColors: (invertLabelColors: boolean) => void;
  toggleCategory: (category: string) => void;
  setVisibleHands: (hands: string[], rangeString: string) => void;
  clearHandFilter: () => void;
  setDeadCards: (cards: string[], inputString: string) => void;
  clearDeadCards: () => void;
  resetConfig: () => void;
}

/**
 * Main chart store using Zustand with localStorage persistence
 */
export const useChartStore = create<ChartStore>()(
  persist(
    (set) => ({
      config: DEFAULT_CHART_CONFIG,
      colorSchemeId: 'HEATMAP_BLUE_RED',
      hiddenCategories: new Set<string>(),
      visibleHands: null,
      rangeFilterString: '',
      deadCards: new Set<string>(),
      deadCardsString: '',

      setColorScheme: (scheme: ColorScheme, id?: ColorSchemeKey) => {
        set((state) => ({
          config: { ...state.config, colorScheme: scheme },
          colorSchemeId: id || state.colorSchemeId,
        }));
      },

      setUseLogScale: (useLogScale: boolean) => {
        set((state) => ({
          config: { ...state.config, useLogScale },
        }));
      },

      setShowValues: (showValues: boolean) => {
        set((state) => ({
          config: { ...state.config, showValues },
        }));
      },

      setShowLegend: (showLegend: boolean) => {
        set((state) => ({
          config: { ...state.config, showLegend },
        }));
      },

      setMaxHeight: (maxHeight: number) => {
        set((state) => ({
          config: { ...state.config, maxHeight },
        }));
      },

      setSegmentLayout: (segmentLayout: ChartConfiguration['segmentLayout']) => {
        set((state) => ({
          config: { ...state.config, segmentLayout },
        }));
      },

      setSegmentGap: (segmentGap: number) => {
        set((state) => ({
          config: { ...state.config, segmentGap },
        }));
      },

      setInvertLabelColors: (invertLabelColors: boolean) => {
        set((state) => ({
          config: { ...state.config, invertLabelColors },
        }));
      },

      toggleCategory: (category: string) => {
        set((state) => {
          const newHidden = new Set(state.hiddenCategories);
          if (newHidden.has(category)) {
            newHidden.delete(category);
          } else {
            newHidden.add(category);
          }
          return { hiddenCategories: newHidden };
        });
      },

      setVisibleHands: (hands: string[], rangeString: string) => {
        set({ visibleHands: new Set(hands), rangeFilterString: rangeString });
      },

      clearHandFilter: () => {
        set({ visibleHands: null, rangeFilterString: '' });
      },

      setDeadCards: (cards: string[], inputString: string) => {
        set({ deadCards: new Set(cards), deadCardsString: inputString });
      },

      clearDeadCards: () => {
        set({ deadCards: new Set<string>(), deadCardsString: '' });
      },

      resetConfig: () => {
        set({
          config: DEFAULT_CHART_CONFIG,
          colorSchemeId: 'HEATMAP_BLUE_RED',
          hiddenCategories: new Set<string>(),
          visibleHands: null,
          rangeFilterString: '',
          deadCards: new Set<string>(),
          deadCardsString: '',
        });
      },
    }),
    {
      name: 'chart-settings',
      partialize: (state) => ({
        colorSchemeId: state.colorSchemeId,
        config: {
          useLogScale: state.config.useLogScale,
          showValues: state.config.showValues,
          showLegend: state.config.showLegend,
          maxHeight: state.config.maxHeight,
          segmentLayout: state.config.segmentLayout,
          segmentGap: state.config.segmentGap,
          invertLabelColors: state.config.invertLabelColors,
        },
      }),
      merge: (persistedState, currentState) => {
        // biome-ignore lint/suspicious/noExplicitAny: persistedState type is unknown from zustand
        const persisted = persistedState as any;
        if (persisted?.colorSchemeId && persisted?.config) {
          // Restore color scheme from ID
          const schemeId = persisted.colorSchemeId as ColorSchemeKey;
          const scheme = COLOR_SCHEMES[schemeId];
          return {
            ...currentState,
            colorSchemeId: schemeId,
            config: {
              ...currentState.config,
              ...persisted.config,
              colorScheme: scheme || currentState.config.colorScheme,
            },
          };
        }
        return currentState;
      },
    },
  ),
);
